import './AuditHistory.scss';

import { ColDef } from '@amzn/gft-ag-grid-enterprise';
import { GFTSearchInput } from '@amzn/gft-frontend-commons';
import { AurigaGrid, AurigaGridRef } from '@components/Common/AurigaGrid/AurigaGrid';
import AurigaLoader from '@components/Common/AurigaLoader';
import { globalGridDefaults } from '@constants/agGrid';
import { useAuditHistoryContext } from '@context/AuditHistoryContext';
import { useDecklinesContext } from '@context/DecklinesContext';
import useRecordLoadComplete from '@hooks/useRecordLoadComplete';
import DownloadIcon from '@mui/icons-material/Download';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import { createId } from '@paralleldrive/cuid2';
import { format } from 'date-fns';
import { isEmpty, isNil } from 'lodash';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { AuditHistoryProps, AuditHistoryViewType, HistoryItemMetadata } from 'types/AuditHistory.types';

const AuditHistory = ({
  deckId: deckIdProp,
  pageName: pageNameProp,
  decklineId: decklineIdProp,
}: AuditHistoryProps = {}) => {
  const { deckId: deckIdParam, pageName: pageNameParam, decklineId: decklineIdParam } = useParams();

  // Props take precedence over URL params, supporting both routed and inline rendering
  const deckId = deckIdProp ?? deckIdParam;
  const pageName = pageNameProp ?? pageNameParam;
  const decklineId = decklineIdProp ?? decklineIdParam;
  const { deckAuditHistory, pageAuditHistory, isLoading, isFetchingNextPageAuditHistory } = useAuditHistoryContext();
  const { pageDecklines } = useDecklinesContext();
  // Sets the minimum number of characters needed for Search. Can adjust as needed.
  const MINIMUM_SEARCH_TERM_LENGTH = 2;
  // For tracking Search across the current Page
  const [searchTerm, setSearchTerm] = useState('');
  // For tracking the current Audit History View Type (i.e., view that invoked the display of Audit History)
  const [auditHistoryViewType, setAuditHistoryViewType] = useState<AuditHistoryViewType | null>(null);
  useRecordLoadComplete(!isLoading && auditHistoryViewType !== null);
  const [filteredRowCount, setFilteredRowCount] = useState<number>(0);
  // To control grid refresh as needed
  const gridRef = useRef<AurigaGridRef>(null); // Grid ref for API access

  /**
   * Effect to determine Audit History Type in view (based on modal route)
   */
  useEffect(() => {
    if (decklineId) {
      setAuditHistoryViewType(AuditHistoryViewType.DECKLINE_VIEW);
    } else if (pageName) {
      setAuditHistoryViewType(AuditHistoryViewType.PAGE_VIEW);
    } else if (deckId) {
      setAuditHistoryViewType(AuditHistoryViewType.DECK_VIEW);
    }
  }, [deckId, decklineId, pageName]);

  /**
   * Memoized filtered Audit History data to be displayed in the grid, based on the current Audit History View Type
   */
  const filteredAuditHistory = useMemo(() => {
    // Provide the full set of available Deck audit history from the context
    if (auditHistoryViewType === AuditHistoryViewType.DECK_VIEW) {
      return deckAuditHistory;
    }

    // Return the full Page Audit history available
    if (auditHistoryViewType === AuditHistoryViewType.PAGE_VIEW) {
      return pageAuditHistory;
    }

    // Filter Audit history for specific Deckline when in Deckline modal route
    if (auditHistoryViewType === AuditHistoryViewType.DECKLINE_VIEW) {
      return pageAuditHistory.filter((historyItem) => {
        const historyItemMetadata = historyItem.row as HistoryItemMetadata;
        return historyItem.row ? historyItemMetadata.itemId === decklineId : false;
      });
    }

    // Default to an empty array if no match on audit history type
    return [];
  }, [auditHistoryViewType, deckAuditHistory, pageAuditHistory, decklineId]);

  const columnDefs: ColDef[] = [
    // Add Page column for deck-level audit history (when no pageName param)
    ...(!pageName
      ? [
          {
            field: 'page_name',
            headerName: 'Page',
            flex: 1,
            maxWidth: 200,
          },
        ]
      : []),
    // Add Deckline column for deck-level, or page-level audit history (when pageName exists but not decklineId)
    ...((deckId || pageName) && !decklineId
      ? [
          {
            field: 'row',
            headerName: 'Item Name',
            flex: 1,
            maxWidth: 200,
            valueGetter: (params: { data: { row: HistoryItemMetadata | string } }) => {
              // Format the display name value, if possible, from the metadata attached to the history item
              let itemDisplayName: string = '';

              const currRowMetadata = params.data?.row as HistoryItemMetadata;
              if (currRowMetadata?.displayName) {
                itemDisplayName = currRowMetadata?.displayName;
              }

              if (isNil(itemDisplayName || isEmpty(itemDisplayName))) {
                // Handle DecklineHistory lookup from context, in the case displayName metadata was missing
                if (pageDecklines && currRowMetadata?.itemId) {
                  const deckline = pageDecklines.find(
                    (currDeckline) => currDeckline.deckline_id === currRowMetadata.itemId,
                  );
                  return deckline?.kpi_id === '|BlankLine|' ? 'Blank Line' : deckline?.metadata?.actual_kpi_name;
                }

                // TODO: Add missing display name handling for DeckHistory item type once captured
              }

              return itemDisplayName || '-';
            },
          },
        ]
      : []),
    {
      field: 'change_type',
      headerName: 'Change Type',
      flex: 1,
    },
    {
      field: 'before',
      headerName: 'Previous Value',
      flex: 2,
      maxWidth: 500,
      wrapText: true,
      autoHeight: true,
    },
    {
      field: 'after',
      headerName: 'Updated Value',
      flex: 2,
      maxWidth: 500,
      wrapText: true,
      autoHeight: true,
    },
    {
      field: 'user',
      headerName: 'User Id',
      flex: 1,
    },
    {
      field: 'dateTime',
      headerName: 'Updated At',
      flex: 2,
      sort: 'desc',
      valueFormatter: (params) => {
        // Format date (in local timezone) if exists
        return params.value ? format(params.value, 'MM/dd/yyyy hh:mm aa') : '-';
      },
    },
    {
      field: 'row.itemType',
      headerName: 'Item Type',
      flex: 1,
    },
  ];

  const defaultColDef = {
    sortable: true,
    filter: true,
  };

  /**
   * Handles search term input changes for AG Grid quickFilter
   * @param value - The search text to filter grid rows
   */
  const handleSearchChange = useCallback((value: string) => {
    setSearchTerm(value);
  }, []);

  /**
   * Resets the Search state
   */
  const handleClearSearch = useCallback(() => {
    setSearchTerm('');
  }, []);

  /**
   * Generates a filename for the Audit History export, based on the current view type
   * @returns string - The filename to use for the export
   */
  const getExportFilename = useCallback(() => {
    const exportAuditHistoryId = createId();

    switch (auditHistoryViewType) {
      case AuditHistoryViewType.DECK_VIEW:
        return `Auriga_Audit_History_Deck_${deckId}_${exportAuditHistoryId}.csv`;
      case AuditHistoryViewType.PAGE_VIEW:
        return `Auriga_Audit_History_Deck_${deckId}_Page_${pageName}_${exportAuditHistoryId}.csv`;
      case AuditHistoryViewType.DECKLINE_VIEW:
        return `Auriga_Audit_History_Deck_${deckId}_Page_${pageName}_Deckline_${decklineId}_${exportAuditHistoryId}.csv`;
      default:
        return `Auriga_Audit_History_${auditHistoryViewType}_${exportAuditHistoryId}.csv`;
    }
  }, [auditHistoryViewType, deckId, decklineId, pageName]);

  if (isLoading || isNil(auditHistoryViewType)) {
    return <AurigaLoader boxClassName='data-loading-container' loaderSize={40} loaderText='Loading audit history...' />;
  }

  return (
    <Box className='audit-history-container'>
      <Box className='audit-history-controls'>
        <Box className='left-action-controls'>
          <GFTSearchInput
            className='audit-history-search-input'
            placeholder='Search...'
            minSearchLength={MINIMUM_SEARCH_TERM_LENGTH}
            value={searchTerm}
            onChange={handleSearchChange}
            onClear={handleClearSearch}
          />
          {searchTerm.length >= MINIMUM_SEARCH_TERM_LENGTH && filteredRowCount > 0 && (
            <Box className='audit-search-results-text'>
              <Typography variant='body1'>{`${filteredRowCount} results found.`}</Typography>
            </Box>
          )}
          {isFetchingNextPageAuditHistory && (
            <Box className='audit-search-results-text'>
              <Typography variant='body2'>Loading more history...</Typography>
            </Box>
          )}
        </Box>
        <Box className='right-action-controls'>
          <Button
            className='audit-history-download-button'
            variant='outlined'
            size='medium'
            startIcon={<DownloadIcon />}
            onClick={() => {
              gridRef.current?.api?.exportDataAsCsv({
                fileName: getExportFilename(),
              });
            }}
          >
            Download CSV
          </Button>
        </Box>
      </Box>
      <Box className='grid-container table-container'>
        <AurigaGrid
          {...globalGridDefaults}
          ref={gridRef}
          rowData={filteredAuditHistory}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          pagination
          paginationPageSize={25} // Should always be <= the hook's default limit (currently 50). Adjust both if changing page size.
          quickFilterText={searchTerm.length >= MINIMUM_SEARCH_TERM_LENGTH ? searchTerm : undefined}
          onFilterChanged={(params) => setFilteredRowCount(params.api.getDisplayedRowCount())}
          onFirstDataRendered={(params) => {
            params.api.sizeColumnsToFit();
            params.api.autoSizeAllColumns(false);
          }}
        />
      </Box>
    </Box>
  );
};

export default AuditHistory;
